import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../hooks/useTheme";
import AppSidebar from "./AppSidebar";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import {
  toggleTodo,
  updateTodo,
  deleteTodo,
  updateTodoAsync,
  deleteTodoAsync,
} from "../store/slices/todosSlice";
import ConfirmationModal from "./ConfirmationModal";
import { BingoAnimation } from "../components/animations";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { useToastContext } from "./ToastContext";

const getInitials = (
  name?: string | null,
  username?: string | null,
): string => {
  if (name)
    return name
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  if (username) return username.charAt(0).toUpperCase();
  return "U";
};

const TodoList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, setTheme } = useTheme();
  const userProfile = useAppSelector((state) => state.user.profile);
  const dispatch = useAppDispatch();
  const { items: todos, loading } = useAppSelector((state) => state.todos);
  const initials = getInitials(userProfile?.name, userProfile?.username);

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];
  const { addToast } = useToastContext();
  const [activeTab, setActiveTab] = useState<"pending" | "completed">(
    "pending",
  );

  // State for editing
  const [editingTodoId, setEditingTodoId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editPriority, setEditPriority] = useState<
    "low" | "medium" | "high" | undefined
  >();

  // State for confirmation modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [onConfirmAction, setOnConfirmAction] = useState<(() => void) | null>(
    null,
  );

  // State for bingo animation
  const [bingoAnimation, setBingoAnimation] = useState<{
    type: "daily" | "weekly";
    show: boolean;
  } | null>(null);

  useEffect(() => {
    trackPageVisit("todo_list", {
      total_todos: todos.length,
      pending_todos: todos.filter((t: any) => !t.completed).length,
      completed_todos: todos.filter((t: any) => t.completed).length,
    });
  }, [todos]);

  const filteredTodos = useMemo(() => {
    return todos.filter((todo: any) => {
      if (activeTab === "pending") {
        return !todo.completed;
      } else {
        return todo.completed;
      }
    });
  }, [todos, activeTab]);

  const pendingTodosCount = useMemo(
    () => todos.filter((todo: any) => !todo.completed).length,
    [todos],
  );

  const completedTodosCount = useMemo(
    () => todos.filter((todo: any) => todo.completed).length,
    [todos],
  );

  const completionPercentage = useMemo(() => {
    if (todos.length === 0) return 0;
    return Math.round((completedTodosCount / todos.length) * 100);
  }, [todos.length, completedTodosCount]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case "high":
        return "text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30";
      case "medium":
        return "text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30";
      case "low":
        return "text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30";
      default:
        return "text-gray-400 dark:text-white/40 bg-black/[0.06] dark:bg-white/[0.06]";
    }
  };

  const getPriorityAccent = (priority?: string) => {
    switch (priority) {
      case "high":
        return "border-l-red-400/80 dark:border-l-red-400/70";
      case "medium":
        return "border-l-[#f5a623]/90 dark:border-l-[#f5a623]/80";
      case "low":
        return "border-l-emerald-400/80 dark:border-l-emerald-400/70";
      default:
        return "border-l-black/10 dark:border-l-white/10";
    }
  };

  const showConfirmationModal = (message: string, onConfirm: () => void) => {
    setModalMessage(message);
    setOnConfirmAction(() => onConfirm);
    setIsModalOpen(true);
  };

  const handleModalConfirm = () => {
    if (onConfirmAction) {
      onConfirmAction();
    }
    setIsModalOpen(false);
    setOnConfirmAction(null);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setOnConfirmAction(null);
  };

  const handleToggleTodo = async (
    todoId: string,
    currentCompleted: boolean,
  ) => {
    const newCompleted = !currentCompleted;
    // Toggle locally first for immediate UI feedback
    dispatch(toggleTodo(todoId));

    try {
      // Update on backend and get the result
      const result = await dispatch(
        updateTodoAsync({ id: todoId, updates: { completed: newCompleted } }),
      ).unwrap();

      // Check for bingo rewards
      if (result.dailyBingoAwarded) {
        setBingoAnimation({ type: "daily", show: true });
      } else if (result.weeklyBingoAwarded) {
        setBingoAnimation({ type: "weekly", show: true });
      }

      // Show toast if points earned
      if (result.pointsEarned > 0) {
        addToast(
          t("toast.pointsEarned", { points: result.pointsEarned }),
          "success",
        );
      }
    } catch (error) {
      console.error("Failed to update todo:", error);
      // Revert local change on error
      dispatch(toggleTodo(todoId));
    }

    // Track todo completion event
    trackUserInteraction(
      newCompleted ? "complete_todo" : "uncomplete_todo",
      "todo_management",
      todoId,
    );
  };

  const handleEditTodo = (todo: any) => {
    setEditingTodoId(todo.id);
    setEditTitle(todo.title);
    setEditPriority(todo.priority);
  };

  const handleSaveEdit = (todoId: string) => {
    if (!editTitle.trim()) return;

    const updates: Partial<any> = {
      title: editTitle.trim(),
      priority: editPriority,
    };

    // Update locally first for immediate UI feedback
    dispatch(updateTodo({ id: todoId, updates }));
    // Then update on backend
    dispatch(updateTodoAsync({ id: todoId, updates }));

    // Track todo editing event
    trackUserInteraction("edit_todo", "todo_management", todoId);

    // Reset edit state
    setEditingTodoId(null);
    setEditTitle("");
    setEditPriority(undefined);
  };

  const handleCancelEdit = () => {
    setEditingTodoId(null);
    setEditTitle("");
    setEditPriority(undefined);
  };

  const handleDeleteTodo = (todoId: string) => {
    showConfirmationModal(t("calendar.confirmDeleteTodo"), () => {
      // Delete locally first for immediate UI feedback
      dispatch(deleteTodo(todoId));
      // Then delete on backend
      dispatch(deleteTodoAsync(todoId));

      // Track todo deletion event
      trackUserInteraction("delete_todo", "todo_management", todoId);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#f5a623] rounded-2xl mb-6">
            <svg
              className="animate-spin w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <h2 className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white mb-2">
            {t("todoList.loadingTodos")}
          </h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaHead
        title="Todo List - Streak Spark"
        description="Manage your todos in Streak Spark. View, edit, and organize your tasks with ease."
        keywords="todo, tasks, list, productivity, habit tracking"
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={() => navigate("/app")}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 border border-[#3b5cde]/30 flex items-center justify-center transition-all hover:bg-[#3b5cde]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("todoList.headerTitle")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={() => navigate("/app")}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#3b5cde]/15 border border-[#3b5cde]/25 flex items-center justify-center transition-all hover:bg-[#3b5cde]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("todoList.headerTitle")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="max-w-4xl mx-auto mb-6 space-y-4">
              <div className="glass-card p-4 lg:p-5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-black/[0.08] dark:border-white/[0.08] bg-white/70 dark:bg-white/[0.03] px-4 py-3">
                    <p className="text-[15px] font-medium uppercase tracking-wide text-neutral-500 dark:text-white/45">
                      {t("todoList.pending")}
                    </p>
                    <p className="mt-1 text-2xl font-semibold tracking-tight text-neutral-900 dark:text-white">
                      {pendingTodosCount}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-black/[0.08] dark:border-white/[0.08] bg-white/70 dark:bg-white/[0.03] px-4 py-3">
                    <p className="text-[15px] font-medium uppercase tracking-wide text-neutral-500 dark:text-white/45">
                      {t("todoList.completed")}
                    </p>
                    <p className="mt-1 text-2xl font-semibold tracking-tight text-neutral-900 dark:text-white">
                      {completedTodosCount}
                    </p>
                  </div>
                </div>
                <div className="mt-4 rounded-2xl border border-black/[0.08] dark:border-white/[0.08] bg-white/70 dark:bg-white/[0.03] px-4 py-3">
                  <div className="h-2 w-full rounded-full bg-black/[0.07] dark:bg-white/[0.08] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#f5a623] to-[#e87b3f] transition-all duration-300"
                      style={{ width: `${completionPercentage}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="glass-card p-1.5">
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setActiveTab("pending")}
                    className={`flex-1 py-2.5 px-4 rounded-xl text-[18px] font-medium transition-all duration-150 ${
                      activeTab === "pending"
                        ? "bg-[#f5a623]/15 text-[#f5a623] border border-[#f5a623]/40 shadow-[0_4px_10px_rgba(245,166,35,0.15)]"
                        : "text-gray-500 dark:text-white/40 hover:text-[#f5a623] dark:hover:text-[#f5a623] border border-transparent"
                    }`}
                  >
                    {t("todoList.pending")} ({pendingTodosCount})
                  </button>
                  <button
                    onClick={() => setActiveTab("completed")}
                    className={`flex-1 py-2.5 px-4 rounded-xl text-[18px] font-medium transition-all duration-150 ${
                      activeTab === "completed"
                        ? "bg-[#f5a623]/15 text-[#f5a623] border border-[#f5a623]/40 shadow-[0_4px_10px_rgba(245,166,35,0.15)]"
                        : "text-gray-500 dark:text-white/40 hover:text-[#f5a623] dark:hover:text-[#f5a623] border border-transparent"
                    }`}
                  >
                    {t("todoList.completed")} ({completedTodosCount})
                  </button>
                </div>
              </div>
            </div>

            {/* Todo List */}
            {/* Todo List */}
            <div className="max-w-4xl mx-auto pb-8">
              {filteredTodos.length === 0 ? (
                <div className="glass-card p-10 lg:p-12 text-center">
                  <div className="w-16 h-16 bg-black/[0.06] dark:bg-white/[0.06] rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <svg
                      className="w-8 h-8 text-neutral-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                  </div>
                  <h3 className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white mb-2">
                    {t("todoList.noTodos", { tab: t(`todoList.${activeTab}`) })}
                  </h3>
                  <p className="text-[17px] text-gray-400 dark:text-white/40">
                    {activeTab === "pending"
                      ? t("todoList.allCompletedMessage")
                      : t("todoList.noCompletedMessage")}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTodos.map((todo: any) => (
                    <div
                      key={todo.id}
                      className={`glass-card p-3.5 lg:p-4 border-l-2 ${getPriorityAccent(todo.priority)} hover:bg-black/[0.02] dark:hover:bg-white/[0.06] transition-colors duration-150`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Checkbox for marking as completed */}
                        <input
                          type="checkbox"
                          checked={todo.completed}
                          onChange={() =>
                            handleToggleTodo(todo.id, todo.completed)
                          }
                          className="w-5 h-5 accent-[#378ADD] rounded border-black/[0.15] dark:border-white/[0.15] mt-0.5 shrink-0"
                        />

                        <div className="flex-1">
                          {editingTodoId === todo.id ? (
                            <div className="space-y-3">
                              <input
                                type="text"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                className="modern-input"
                                placeholder={
                                  t("todoList.todoTitlePlaceholder") as string
                                }
                                autoFocus
                              />
                              <div className="flex items-center space-x-3">
                                <select
                                  value={editPriority || ""}
                                  onChange={(e) =>
                                    setEditPriority(
                                      (e.target.value as
                                        | "low"
                                        | "medium"
                                        | "high") || undefined,
                                    )
                                  }
                                  className="modern-input text-[17px] py-2"
                                >
                                  <option value="">
                                    {t("todoList.noPriority")}
                                  </option>
                                  <option value="low">
                                    {t("todoList.low")}
                                  </option>
                                  <option value="medium">
                                    {t("todoList.medium")}
                                  </option>
                                  <option value="high">
                                    {t("todoList.high")}
                                  </option>
                                </select>
                                <button
                                  onClick={() => handleSaveEdit(todo.id)}
                                  className="px-4 py-2 text-[19px] font-semibold bg-[#f5a623] text-[#1a1300] rounded-xl border border-[#f5a623] hover:opacity-90 transition-all duration-150"
                                >
                                  {t("todoList.save")}
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-4 py-2 text-[19px] font-medium bg-white dark:bg-white/[0.04] text-neutral-700 dark:text-white/70 rounded-xl border border-black/[0.08] dark:border-white/[0.1] hover:bg-black/[0.04] dark:hover:bg-white/[0.09] transition-colors"
                                >
                                  {t("todoList.cancel")}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <h3
                                    className={`text-[19px] font-semibold tracking-tight ${
                                      todo.completed
                                        ? "line-through text-gray-400 dark:text-white/30"
                                        : "text-gray-900 dark:text-white"
                                    }`}
                                  >
                                    {todo.title}
                                  </h3>
                                  {todo.priority && (
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-[15px] font-medium ${getPriorityColor(
                                        todo.priority,
                                      )}`}
                                    >
                                      {todo.priority.charAt(0).toUpperCase() +
                                        todo.priority.slice(1)}
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center gap-1.5 shrink-0">
                                  <div
                                    className={`w-2 h-2 rounded-full ${
                                      todo.completed
                                        ? "bg-[#22c67a]"
                                        : "bg-[#f5a623]"
                                    }`}
                                  ></div>
                                  <span
                                    className={`text-[15px] font-medium ${
                                      todo.completed
                                        ? "text-[#22c67a]"
                                        : "text-[#f5a623]"
                                    }`}
                                  >
                                    {todo.completed
                                      ? t("todoList.statusCompleted")
                                      : t("todoList.statusPending")}
                                  </span>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1.5">
                                    <svg
                                      className="w-3.5 h-3.5 text-gray-400 dark:text-white/30 shrink-0"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                      />
                                    </svg>
                                    <span className="text-[16px] text-gray-400 dark:text-white/40">
                                      {t("todoList.createdFor")}{" "}
                                      <span className="font-medium text-gray-700 dark:text-white/70">
                                        {todo.date}
                                      </span>
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <svg
                                      className="w-3.5 h-3.5 text-gray-400 dark:text-white/30 shrink-0"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                      />
                                    </svg>
                                    <span className="text-[16px] text-gray-400 dark:text-white/40">
                                      {t("todoList.created")}{" "}
                                      <span className="font-medium text-gray-700 dark:text-white/70">
                                        {formatDate(todo.createdAt)}
                                      </span>
                                    </span>
                                  </div>
                                </div>

                                <div className="space-y-1">
                                  {todo.completed && todo.completedAt && (
                                    <div className="flex items-center gap-1.5">
                                      <svg
                                        className="w-3.5 h-3.5 text-[#22c67a] shrink-0"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                      <span className="text-[16px] text-gray-400 dark:text-white/40">
                                        {t("todoList.completed")}{" "}
                                        <span className="font-medium text-gray-700 dark:text-white/70">
                                          {formatDate(todo.completedAt)}
                                        </span>
                                      </span>
                                    </div>
                                  )}
                                  <div className="h-4" />
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Action buttons - only show when not editing */}
                        {editingTodoId !== todo.id && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => handleEditTodo(todo)}
                              className="p-2 text-gray-400 dark:text-white/30 hover:text-[#378ADD] hover:bg-[#378ADD]/10 transition-colors rounded-lg"
                              title={t("todoList.editTodoTitle") as string}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteTodo(todo.id)}
                              className="p-2 text-gray-400 dark:text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors rounded-lg"
                              title={t("todoList.deleteTodoTitle") as string}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
              isOpen={isModalOpen}
              message={modalMessage}
              onConfirm={handleModalConfirm}
              onCancel={handleModalCancel}
            />

            {/* Bingo Animation */}
            {bingoAnimation?.show && (
              <BingoAnimation
                type={bingoAnimation.type}
                onComplete={() => setBingoAnimation(null)}
              />
            )}
          </div>
        </main>

        {/* Floating Add Todo Button */}
        <button
          onClick={() => navigate("/add-todo")}
          className="fixed bottom-24 right-6 z-50 w-14 h-14 bg-[#f5a623] hover:opacity-90 text-[#1a1300] rounded-full border border-[#f5a623] transition-all duration-150 flex items-center justify-center touch-manipulation lg:bottom-8 lg:right-8"
          aria-label={t("todoList.addTodoButton") as string}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
        </button>

        {/* MOBILE BOTTOM NAV */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border-t border-black/[0.08] dark:border-white/[0.08]">
          <div className="flex items-center justify-around px-2 pt-2 pb-2">
            {mobileNavLinks.map(({ path, emoji, label }) => {
              const active = isNavActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex flex-col items-center gap-1 cursor-pointer"
                  style={{ color: active ? "#f5a623" : undefined }}
                >
                  <span className="text-[22px]">{emoji}</span>
                  <span
                    className={`text-[13px] font-medium${active ? "" : " text-gray-400 dark:text-white/25"}`}
                    style={active ? { color: "#f5a623" } : undefined}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default TodoList;
